import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Auto-refresh token when expired
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      if (error.response?.status === 401 && 
          error.response?.data?.detail === 'Token expired' && 
          !original._retry) {
        original._retry = true;
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          // ✅ Update token in localStorage so next requests use new token
          if (data.token) {
            localStorage.setItem('token', data.token);
            // ✅ Update the failed request's Authorization header with new token
            original.headers['Authorization'] = `Bearer ${data.token}`;
          }
          return axios(original);
        } catch {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timeoutRef = useRef(null);

  const performLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('token');
    setUser(false);
  }, []);

  // Auto-logout on inactivity
  useEffect(() => {
    if (!user || user === false) return;

    const resetTimer = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        performLogout();
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, performLogout]);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // No token in localStorage, try refresh cookie
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          if (data.token) {
            localStorage.setItem('token', data.token);
            setUser(data);
            return;
          }
        } catch {
          setUser(false);
          setLoading(false);
          return;
        }
      }
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(data);
    } catch (e) {
      // Token expired → try refresh
      if (e.response?.data?.detail === 'Token expired') {
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          if (data.token) {
            localStorage.setItem('token', data.token);
            setUser(data);
            return;
          }
        } catch {
          // Refresh also failed → logout
        }
      }
      localStorage.removeItem('token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, captchaToken = '', honeypot = '') => {
    try {
      const { data } = await axios.post(`${API}/auth/login`,
        { email, password, captcha_token: captchaToken, honeypot },
        { withCredentials: true });
      localStorage.setItem('token', data.token);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password, phone, captchaToken = '', honeypot = '') => {
    try {
      const { data } = await axios.post(`${API}/auth/register`,
        { name, email, password, phone, captcha_token: captchaToken, honeypot },
        { withCredentials: true });
      localStorage.setItem('token', data.token);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = performLogout;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export { API };
